import { TableRowCallForActionButton } from 'components/buttons/CallForActionButton';
import ListCreateButton from 'components/buttons/ListCreateButton';
import { DraftChip } from 'components/drafts/DraftChip';
import { DraftRowMenu } from 'components/drafts/DraftRecordActions';
import { useWithDrafts } from 'components/drafts/useWithDrafts';
import { MoreVert as MoreVertIcon, SavingsOutlined } from 'components/icons';
import Loader from 'components/Loader';
import MatterIndicators from 'components/matters/MatterIndicators';
import { FormattedDate } from 'components/misc/FormattedDate';
import ConfirmationModal from 'components/modals/confirmation/ConfirmationModal';
import { useModal } from 'components/modals/useModal';
import { RecordLink } from 'components/record-chip/RecordLink';
import type { DataTableColumnDef } from 'components/tables/data-table';
import ListTableWithFilters from 'components/tables/list-table/ListTableWithFilters';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from 'components/ui/dropdown-menu';
import MoneyDisplay from 'components/util/MoneyDisplay';
import { SyntheticEvent, useCallback, useMemo } from 'react';
import {
    useDeletePartyAssetMutation,
    useGetPartyAssetsTotalsQuery,
    useLazyGetPartyAssetsCountQuery,
    useLazyGetPartyAssetsWithFiltersQuery,
} from 'store/api/partyAssets';
import { FeatureFlag } from 'types/features';
import { t } from 'utils/getTranslations';
import { useClassifiers } from 'utils/useClassifiers';
import { useNavigateWithReturnState } from 'utils/useNavigateWithReturnState';
import { AssetTotals } from './AssetTotals';
import { ClientAssetsTableVariant, getClientAssetsFilterConfig } from './clientAssetsFilterConfig';
import { PartyAsset, PartyAssetCurrencyTotal } from './types';
import { usePartyAssetPermissions } from './usePartyAssetPermissions';

const USED_CLASSIFIERS = ['CURRENCY'];

// `entity-link` hands the cell's link colour and wrapping to the table's own rule.
const LINK_CLASS = 'entity-link text-fg hover:text-link w-fit no-underline hover:underline';

type AssetColumn = DataTableColumnDef<PartyAsset, PartyAssetCurrencyTotal[] | undefined>;

// The row opens the record, so what is clicked inside it must not reach the row, the menu's portal included.
const stop = (event: SyntheticEvent) => event.stopPropagation();

/** A row's Delete, after "Are you sure?". */
export function useConfirmDeletePartyAsset(): (id: number, onDeleted?: () => void) => void {
    const { openModal, closeModal } = useModal();
    const [deletePartyAsset] = useDeletePartyAssetMutation();
    return (id, onDeleted) =>
        openModal(
            <ConfirmationModal
                content={t('client-assets.delete-confirmation')}
                onCancel={closeModal}
                onConfirm={() => {
                    // The API layer toasts the outcome either way.
                    deletePartyAsset(id)
                        .unwrap()
                        .then(() => onDeleted?.())
                        .catch(() => undefined)
                        .finally(closeModal);
                }}
            />,
            closeModal
        );
}

/** The list's count, with the drafts it lists. */
const useLazyPartyAssetsCountWithDraftsQuery: typeof useLazyGetPartyAssetsCountQuery = (options) => {
    const [count, ...rest] = useLazyGetPartyAssetsCountQuery(options);
    return [useWithDrafts(count), ...rest];
};

function RowActions({ asset }: { asset: PartyAsset }) {
    const { canUpdate, canDelete } = usePartyAssetPermissions();
    const navigateWithReturn = useNavigateWithReturnState();
    const confirmDelete = useConfirmDeletePartyAsset();

    if (asset.isDraft) {
        return (
            <div onClick={stop} onKeyDown={stop}>
                <DraftRowMenu draft={{ type: 'client-asset', id: asset.id }} />
            </div>
        );
    }
    if (!canUpdate && !canDelete) {
        return null;
    }
    return (
        <div onClick={stop} onKeyDown={stop}>
            <DropdownMenu>
                <DropdownMenuTrigger
                    render={
                        <TableRowCallForActionButton shape="rounded" aria-label={t('actions')}>
                            <MoreVertIcon />
                        </TableRowCallForActionButton>
                    }
                />
                <DropdownMenuContent align="end" className="w-auto">
                    {canUpdate ? (
                        <DropdownMenuItem onClick={() => navigateWithReturn(`/client-asset/${asset.id}/edit`)}>
                            {t('edit')}
                        </DropdownMenuItem>
                    ) : null}
                    {canDelete ? (
                        <DropdownMenuItem variant="destructive" onClick={() => confirmDelete(asset.id)}>
                            {t('delete')}
                        </DropdownMenuItem>
                    ) : null}
                </DropdownMenuContent>
            </DropdownMenu>
        </div>
    );
}

interface Props {
    variant: ClientAssetsTableVariant;
    partyId?: string;
    matterId?: string;
}

/** The client asset list: the global page, and the Assets tab of a contact (`partyId`) or a case (`matterId`). */
export function ClientAssetsTable({ variant, partyId, matterId }: Props) {
    const navigateWithReturn = useNavigateWithReturnState();
    const [fetchAssets, { data, isFetching }] = useLazyGetPartyAssetsWithFiltersQuery();
    const fetchData = useWithDrafts(fetchAssets);
    const { classifiers, isLoading: isLoadingClassifiers } = useClassifiers({ codes: USED_CLASSIFIERS });

    // The global list keeps its own header action; the tabs seed the new asset from their parent.
    const addAssetPath =
        variant === 'case-tab' && matterId
            ? `/matter/${matterId}/client-asset/new`
            : variant === 'client-tab' && partyId
              ? `/contact/${partyId}/client-asset/new`
              : undefined;

    const filterConfig = useMemo(
        () => getClientAssetsFilterConfig({ variant, partyId, matterId, currencyClassifier: classifiers['CURRENCY'] }),
        [variant, partyId, matterId, classifiers]
    );

    const openAsset = useCallback(
        (asset: PartyAsset) => navigateWithReturn(`/client-asset/${asset.id}`),
        [navigateWithReturn]
    );

    const columns = useMemo<AssetColumn[]>(
        () => [
            {
                id: 'client',
                accessorKey: 'party.name',
                header: t('client-assets.fields.client'),
                meta: { sortAccessor: 'party.name', widthWeight: 1.4, minPx: 160 },
                cell: ({ row }) => {
                    const { party, isDraft } = row.original;
                    const client = party ? (
                        <RecordLink
                            to={`/contact/${party.id}`}
                            className={LINK_CLASS}
                            onClick={stop}
                            label={party.name ?? ''}
                        />
                    ) : (
                        '-'
                    );
                    return isDraft ? (
                        <div className="flex flex-wrap items-center gap-1.5">
                            <DraftChip />
                            {client}
                        </div>
                    ) : (
                        client
                    );
                },
            },
            {
                id: 'case',
                accessorKey: 'matter.caseNumber',
                header: t('client-assets.fields.case'),
                meta: { sortAccessor: 'matter.caseNumber', widthWeight: 1.4, minPx: 160 },
                cell: ({ row }) => {
                    const { matter } = row.original;
                    return matter ? (
                        <div className="flex items-center gap-1.5">
                            <MatterIndicators isConfidential={matter.isConfidential} />
                            <RecordLink
                                to={`/matter/${matter.id}`}
                                className={LINK_CLASS}
                                onClick={stop}
                                label={[matter.caseNumber, matter.name].filter(Boolean).join(' ')}
                            />
                        </div>
                    ) : (
                        '-'
                    );
                },
            },
            {
                id: 'createdAt',
                accessorKey: 'createdAt',
                header: t('client-assets.fields.created-at'),
                meta: { widthWeight: 0.8, minPx: 110 },
                cell: ({ row }) => <FormattedDate value={row.original.createdAt} nowrap />,
            },
            {
                id: 'bankTransferDate',
                accessorKey: 'bankTransferDate',
                header: t('client-assets.fields.bank-transfer-date'),
                meta: { widthWeight: 0.8, minPx: 120 },
                cell: ({ row }) => <FormattedDate value={row.original.bankTransferDate} nowrap />,
            },
            {
                id: 'transferAmount',
                accessorKey: 'transferAmount',
                header: t('client-assets.fields.transfer-amount'),
                meta: {
                    widthWeight: 1,
                    minPx: 130,
                    textAlign: 'right',
                    renderTotal: (totals) => (totals ? <AssetTotals totals={totals} /> : null),
                },
                // A draft moves no money, and the totals leave it out.
                cell: ({ row }) =>
                    row.original.isDraft ? '—' : <MoneyDisplay amount={row.original.transferAmount ?? 0} />,
            },
            {
                id: 'currency',
                accessorKey: 'currency',
                header: t('client-assets.fields.currency'),
                meta: { widthWeight: 0.5, minPx: 90 },
            },
            {
                id: 'note',
                accessorKey: 'note',
                header: t('client-assets.fields.note'),
                meta: { widthWeight: 2, minPx: 180 },
            },
            {
                id: 'actions',
                header: '',
                enableSorting: false,
                meta: { fixedPx: 60 },
                cell: ({ row }) => <RowActions asset={row.original} />,
            },
        ],
        []
    );

    if (isLoadingClassifiers) {
        return <Loader />;
    }

    const list = (
        <ListTableWithFilters<PartyAsset>
            columnConfigId={`client-assets-${variant}`}
            columns={columns}
            data={data}
            isLoading={isFetching}
            pluralTextId="client-assets.records"
            singularTextId="client-assets.record"
            noEntriesId="client-assets.no-records"
            noEntriesIcon={SavingsOutlined}
            fetchData={fetchData}
            fetchCount={useLazyPartyAssetsCountWithDraftsQuery}
            fetchTotals={useGetPartyAssetsTotalsQuery}
            // One GROUP BY over the filtered set, with no record cap: it starts on, and the toggle only dismisses it.
            onDemandTotals
            defaultTotalsRowRequested
            filterConfig={filterConfig}
            canRememberFilters={variant === 'global'}
            rowsPerPage={50}
            onRowClick={openAsset}
            extraHeaderComponent={
                addAssetPath ? (
                    <ListCreateButton
                        permissions={['CREATE.PARTY_ASSETS']}
                        requiredFeature={FeatureFlag.PARTY_ASSETS}
                        variant="contained"
                        color="secondary"
                        size="small"
                        onClick={() => navigateWithReturn(addAssetPath)}
                        labelId="client-assets.add"
                        newLabelId="client-assets.new-title"
                    />
                ) : undefined
            }
        />
    );

    // A tab's panel is no column of its own: the new design spaces the filter row from the table as the list page
    // does. Classic's count row already carries its margin.
    return variant === 'global' ? list : <div className="new:flex new:flex-col new:gap-4">{list}</div>;
}
